import { Box } from "@sembark-travel/ui-base"
import { useEffect, useRef, useState } from "react"
import { WhatsAppImageDialog } from "./ImageDialog"

/**
 * The images a message's HTML can open full size: a sent or received image
 * or sticker, and a template's header image.
 */
const EXPANDABLE_IMAGE = "img"

/**
 * Drawn small and at its own size, so it opens at that size rather than
 * stretched to the dialog.
 */
const STICKER = ".wa-message-media--sticker"

/**
 * What an image belongs with, innermost first: a carousel card, a template,
 * or a plain message.
 */
const IMAGE_CONTAINER = ".wa-template-carousel-card, .wa-template, .wa-message"

/** The text sent with an image: a message's caption or a template's body. */
const IMAGE_CAPTION = ".wa-message-text, .wa-template-body"

type TExpandedImage = {
	src: string
	alt: string
	/** The caption as the server rendered it, formatting included. */
	captionHtml: string | null
	isNaturalSize: boolean
}

function expandableImage(target: EventTarget): HTMLImageElement | null {
	return target instanceof HTMLImageElement && target.matches(EXPANDABLE_IMAGE)
		? target
		: null
}

function captionHtmlFor(image: HTMLImageElement): string | null {
	const caption = image
		.closest(IMAGE_CONTAINER)
		?.querySelector(IMAGE_CAPTION)
		?.innerHTML.trim()

	return caption || null
}

/**
 * A message's server-rendered HTML. Each image in it opens full size in a
 * dialog. The server's HTML also goes into emails, so the image is made a
 * button here, in the browser: focusable, announced as a button, and opened
 * by a click, Enter or Space.
 */
export function WhatsAppMessageHtml({ html }: { html: string }) {
	const ref = useRef<HTMLDivElement>(null)
	const [expanded, setExpanded] = useState<TExpandedImage | null>(null)

	useEffect(() => {
		ref.current
			?.querySelectorAll<HTMLImageElement>(EXPANDABLE_IMAGE)
			.forEach((image) => {
				image.tabIndex = 0
				image.setAttribute("role", "button")
				image.setAttribute(
					"aria-label",
					image.alt ? `View ${image.alt} full size` : "View image full size"
				)
				image.style.cursor = "zoom-in"
			})
	}, [html])

	function expand(image: HTMLImageElement) {
		setExpanded({
			src: image.src,
			alt: image.alt,
			captionHtml: captionHtmlFor(image),
			isNaturalSize: image.matches(STICKER),
		})
	}

	return (
		<>
			<Box
				ref={ref}
				dangerouslySetInnerHTML={{ __html: html }}
				onClick={(event: React.MouseEvent) => {
					const image = expandableImage(event.target)
					if (image) expand(image)
				}}
				onKeyDown={(event: React.KeyboardEvent) => {
					if (event.key !== "Enter" && event.key !== " ") return
					const image = expandableImage(event.target)
					if (!image) return
					// Space would otherwise scroll the conversation.
					event.preventDefault()
					expand(image)
				}}
			/>
			<WhatsAppImageDialog
				src={expanded?.src ?? null}
				alt={expanded?.alt ?? ""}
				captionHtml={expanded?.captionHtml ?? null}
				isNaturalSize={expanded?.isNaturalSize ?? false}
				onClose={() => setExpanded(null)}
			/>
		</>
	)
}
